
import { defineComponent, reactive, toRefs, onMounted, ref, onActivated } from 'vue'
import { useI18n } from 'vue-i18n'
import { createModel } from '@/utils/component'
import { checkPermission } from '@/utils/permission'
import { formatDate, formatThousands } from '@/utils'
import Container from '@/components/form/container/Container.vue'
import SimpleTable from '@/components/table/simple/SimpleTable.vue'
import SearchForm from '@/components/form/search/SearchForm.vue'
import BaseDialog from '@/components/dialog/BaseDialog.vue'
import { ElMessage } from 'element-plus'
import GenerateApprovalRefundDialog
  from '@/views/application-form/payment-request-refund/GenerateApprovalRefundDialog.vue'
import dayjs from 'dayjs'
import {
  RefundRequestIdListModel, RefundRequestModel, RefundRequestPageParamModel, RefundRequestSetPaymentDateModel
} from '@/model/application-form/paymentRequestRefund'
import {
  cancelRefundApplication, excelRefundFileUpload, exportRefundTemplate, getRefundRequestPageList,
  setRefundRequestPaymentDate, getRefundRequestPageListByUser
} from '@/apis/application-form/paymentRequestRefund'
import router from '@/router'
import { useStore } from 'vuex'
import { RootObject } from '@/model/rootObject'
import { CompanyListParamModel } from '@/model/master-data/companyModel'
import { getCompanyAllList } from '@/apis/main-data/company'

export default defineComponent({
  name: 'PaymentRequestRefundList',
  components: {
    GenerateApprovalRefundDialog,
    BaseDialog,
    SearchForm,
    SimpleTable,
    Container
  },
  setup() {
    const { t } = useI18n()
    const tableRef = ref()
    const selectedRows = ref<RefundRequestModel[]>()
    const searchStruct = [
      {
        prop: 'companyCode',
        label: t('company.companyCode'),
        component: 'el-select',
        type: 'text',
        model: '',
        filterable: true,
        loading: false,
        options: [] as any
      },
      { prop: 'vendorName', label: t('supplier.supplierName'), component: 'el-input', type: 'text', model: '' },
      {
        prop: 'paymentDate',
        label: t('order.paymentDate'),
        component: 'el-date-picker',
        type: 'date',
        model: '',
        suffix: '前'
      },
      { prop: 'currency', label: t('bank.currency'), component: 'el-input', type: 'text', model: '' }
    ]
    const orderTableStruct = [
      { prop: 'id', label: t('common.indexNumber'), width: '80px' },
      { prop: 'companyCode', label: t('company.companyCode') },
      { prop: 'vendorName', label: t('supplier.customerName'), width: '200px' },
      { prop: 'specialPaymentType', label: t('approvalForm.specialPaymentType'), width: '200px' },
      { prop: 'reference', label: t('order.invoiceNumber') },
      { prop: 'amount', label: t('bank.amount'), formatter: (row: any) => formatThousands(row.amount), align: 'right' },
      { prop: 'currency', label: t('bank.currency'), width: '80px' },
      {
        prop: 'paymentDate',
        label: t('order.paymentDate'),
        formatter: (row: RefundRequestModel) => {
          return formatDate(row.paymentDate, 'yyyy-MM-dd')
        }
      },
      { prop: 'comment', label: t('common.notes') },
      { prop: 'purpose', label: t('order.purposePayeeMayView'), width: '200px' }
    ]
    const dataMap = reactive({
      searchFormStruct: searchStruct,
      searchFormModel: createModel(searchStruct) as RefundRequestPageParamModel,
      tableData: Array<RefundRequestModel>(),
      columnStruct: orderTableStruct,
      datePickerDialogVisible: false,
      generateApprovalDialogVisible: false,
      changePurposeId: null as number | null,
      purpose: null as string | null,
      selectedDate: null as Date | null,
      listLoading: true,
      limit: 10,
      total: 0,
      page: 1,
      handleCurrentChange(page?: any) {
        dataMap.getPageList(page)
      },
      handleSizeChange(val: any) {
        dataMap.getPageList(null, val)
      },
      async getPageList(page?: any, limit?: any) {
        if (page) {
          dataMap.page = page
        }
        if (limit) {
          dataMap.limit = limit
        }
        dataMap.listLoading = true
        let dataStr = null
        if (dataMap.searchFormModel.paymentDate) {
          dataStr = dayjs(dataMap.searchFormModel.paymentDate).endOf('day').format('YYYY-MM-DD HH:mm:ss SSS')
        }
        const params: RefundRequestPageParamModel = {
          companyCode: dataMap.searchFormModel.companyCode,
          vendorName: dataMap.searchFormModel.vendorName,
          paymentDate: dataStr,
          currency: dataMap.searchFormModel.currency,
          isProcess: 0,
          active: true,
          pageCurrent: dataMap.page,
          pageSize: dataMap.limit
        }
        // const data = await getRefundRequestPageList(params)
        let data = null
        if (checkPermission(['APPLY_REFUND_R'])) {
          data = await getRefundRequestPageList(params)
        } else if (checkPermission(['APPLY_REFUND_SR'])) {
          data = await getRefundRequestPageListByUser(params)
        }
        if (data && data.data && data.data.list.length > 0) {
          dataMap.tableData = data.data.list
        } else {
          dataMap.tableData = []
        }
        dataMap.total = data?.data.totalCount ?? 0
        tableRef.value.clearSelection()
        // Just to simulate the time of the request
        setTimeout(() => {
          dataMap.listLoading = false
        }, 0.5 * 1000)
      },
      submitSearch() {
        dataMap.page = 1
        dataMap.getPageList()
      },
      resetForm() {
        console.log('reset')
      },
      async handleAvatarSuccess(response: RootObject<number>, file: any) {
        if (response.data > 0) {
          ElMessage({
            message: t('fileUpload.uploadSuccess'),
            type: 'success',
            duration: 2000
          })
          await dataMap.getPageList(dataMap.page, dataMap.limit)
        } else if (response.msg && response.msg !== 'success') {
          ElMessage({
            message: response.msg,
            type: 'error',
            duration: 2000
          })
        } else {
          ElMessage({
            message: file.name + ':' + t('fileUpload.uploadError'),
            type: 'error',
            duration: 2000
          })
        }
      },
      handleAvatarError(response: RootObject<number>, file: any) {
        if (response.msg) {
          ElMessage({
            message: response.msg,
            type: 'error',
            duration: 2000
          })
        } else {
          ElMessage({
            message: file.name + ':' + t('fileUpload.uploadError'),
            type: 'error',
            duration: 2000
          })
        }
      },
      getUploadUri() {
        return excelRefundFileUpload()
      },
      getUploadHeader() {
        return {
          Authorization: useStore().state.user.token
        }
      },
      checkFile(file: any) {
        if (file) {
          console.log('file type')
          console.log(file)
        }
      },
      async handleExport() {
        const urlBlob = await exportRefundTemplate()
        if (urlBlob) {
          const link = document.createElement('a')
          link.href = urlBlob
          link.download = '客户退款申请模板.xlsx'
          link.click()
          window.URL.revokeObjectURL(urlBlob)
        } else {
          ElMessage({
            message: '获取excel失败',
            type: 'error',
            duration: 2000
          })
        }
      }
    })
    const handlePaymentDate = () => {
      if (tableRef.value && tableRef.value.getSelectionRows().length > 0) {
        dataMap.selectedDate = null
        dataMap.datePickerDialogVisible = true
      } else {
        ElMessage({
          message: t('order.selectTip'),
          type: 'error'
        })
      }
    }
    const cancelApplication = async() => {
      if (tableRef.value && tableRef.value.getSelectionRows().length > 0) {
        const params: RefundRequestIdListModel = { idList: tableRef.value.getSelectionRows().map((row: any) => row.id) }
        const res = await cancelRefundApplication(params)
        if (res?.data && res?.data > 0) {
          ElMessage({
            message: t('common.cancelSuccess'),
            type: 'success',
            duration: 2000
          })
          await dataMap.getPageList(null, 10)
        } else {
          ElMessage({
            message: res?.msg,
            type: 'error',
            duration: 2000
          })
        }
      } else {
        ElMessage({
          message: t('order.selectTip'),
          type: 'error'
        })
      }
    }
    const onCreate = () => {
      router.push({ name: 'CreatePaymentRequestRefund' })
    }
    const generateApprovalForm = () => {
      if (tableRef.value && tableRef.value.getSelectionRows().length > 0) {
        selectedRows.value = tableRef.value.getSelectionRows().map((row: any) => row.id)
        dataMap.generateApprovalDialogVisible = true
      } else {
        ElMessage({
          message: t('order.selectTip'),
          type: 'error'
        })
      }
    }
    const handleEdit = (row?: RefundRequestModel) => {
      if (row) {
        router.push({ path: `update/${row.id}` })
      }
    }
    const onDialogConfirm = async() => {
      await dataMap.getPageList(dataMap.page, dataMap.limit)
    }
    const onDatePickerDialogConfirm = async() => {
      if (dataMap.selectedDate != null) {
        const params: RefundRequestSetPaymentDateModel = {
          idList: tableRef.value.getSelectionRows().map((row: any) => row.id),
          paymentDate: formatDate(dataMap.selectedDate)
        }
        const res = await setRefundRequestPaymentDate(params)
        if (res?.data && res?.data > 0) {
          ElMessage({
            message: t('common.setSuccess'),
            type: 'success',
            duration: 2000
          })
          dataMap.datePickerDialogVisible = false
        } else {
          ElMessage({
            message: res?.msg,
            type: 'error',
            duration: 2000
          })
        }
        await dataMap.getPageList(dataMap.page, dataMap.limit)
      } else {
        ElMessage({
          message: t('order.setPaymentDateP'),
          type: 'error'
        })
      }
    }
    const getCompanyList = async() => {
      dataMap.searchFormStruct[0].loading = true
      const params: CompanyListParamModel = {
        name: null,
        companyCode: null
      }
      const data = await getCompanyAllList(params)
      const list = data?.data ?? []
      const optionList = []
      for (const iterator of list) {
        optionList.push({
          label: iterator.companyCode + ', ' + iterator.name,
          value: iterator.companyCode
        })
      }
      dataMap.searchFormStruct[0].options = optionList
      dataMap.searchFormStruct[0].loading = false
    }
    onMounted(() => {
      dataMap.getPageList(null, 10)
      getCompanyList()
    })
    onActivated(async() => {
      if (history.state?.refresh === true) {
        await dataMap.getPageList()
        // 用完删除
        const state = { ...history.state }
        delete state.refresh
        history.replaceState(state, '')
      }
    })
    return {
      t,
      tableRef,
      selectedRows,
      handlePaymentDate,
      cancelApplication,
      onCreate,
      generateApprovalForm,
      handleEdit,
      onDatePickerDialogConfirm,
      onDialogConfirm,
      ...toRefs(dataMap)
    }
  }
})
